"use client";

import { Check } from "lucide-react";
import { useMemo } from "react";
import { useBooking } from "../context";
import {
  dayKey,
  dayKeyToDate,
  dayPeriod,
  durationMinutes,
  daySummary,
  groupByDay,
  hasStarted,
  spotsState,
  type DayPeriod,
  type ScheduleSession,
  type ScheduleWeek,
} from "../schedule";
import { ClassDot, type ClassAction } from "./class-card";

const PERIODS: DayPeriod[] = ["morning", "afternoon", "evening"];

const focusRing =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-studio-ink";

/**
 * The whole week at once: seven columns, one row per part of the day
 * (morning, afternoon, evening). Used by ScheduleView when there is room;
 * narrower, the day strip and list take over.
 */
export function WeekGrid({
  sessions,
  week,
  getAction,
  showColor = false,
}: {
  sessions: ScheduleSession[];
  week: ScheduleWeek;
  getAction: (session: ScheduleSession) => ClassAction;
  /** Class type colour dot before each title. */
  showColor?: boolean;
}) {
  const { t, fmt, timeZone } = useBooking();
  const today = dayKey(new Date(), timeZone);

  const byDay = useMemo(() => groupByDay(sessions, timeZone), [sessions, timeZone]);
  // Rows with no class all week are left out.
  const periods = useMemo(
    () => PERIODS.filter((p) => sessions.some((s) => dayPeriod(s.startTime, timeZone) === p)),
    [sessions, timeZone],
  );

  return (
    <div aria-label={t("schedule.weekGrid")} role="group" className="flex flex-col gap-3">
      <div className="grid grid-cols-7 gap-2">
        {week.days.map((day) => {
          const date = dayKeyToDate(day, timeZone);
          const { open: count, closed } = daySummary(byDay.get(day));
          const isToday = day === today;
          return (
            <div
              key={day}
              aria-current={isToday ? "date" : undefined}
              className={[
                "flex flex-col items-center gap-0.5 rounded-card border px-2 py-2",
                isToday
                  ? "border-studio-accent bg-studio-accent-soft text-studio-ink"
                  : "border-line bg-surface text-ink",
              ].join(" ")}
            >
              <span className={`text-xs font-medium ${isToday ? "" : "text-ashe"}`}>
                {fmt.weekdayShort(date)}
              </span>
              <span className="text-lg font-semibold leading-none tabular-nums">
                {fmt.dayNumber(date)}
              </span>
              <span className={`text-xs ${count ? "" : "text-ink-faint"}`}>
                {closed ? t("schedule.dayClosed") : count ? t("schedule.classCount", { count }) : "—"}
              </span>
            </div>
          );
        })}
      </div>

      {periods.map((period) => (
        <section key={period} aria-label={t(`schedule.${period}`)} className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ashe">
            {t(`schedule.${period}`)}
          </h3>
          <div className="grid grid-cols-7 gap-2">
            {week.days.map((day) => {
              const cell = (byDay.get(day) ?? []).filter(
                (s) => dayPeriod(s.startTime, timeZone) === period,
              );
              return (
                <ul
                  key={day}
                  aria-label={fmt.dayLong(dayKeyToDate(day, timeZone))}
                  className={`flex min-w-0 flex-col gap-2 rounded-control ${day === today ? "bg-studio-accent-soft/50 p-1" : ""}`}
                >
                  {cell.map((session) => (
                    <li key={session.id}>
                      <ClassTile
                        session={session}
                        action={getAction(session)}
                        showColor={showColor}
                      />
                    </li>
                  ))}
                </ul>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/** One class in the grid: the whole tile is the button. */
function ClassTile({
  session,
  action,
  showColor,
}: {
  session: ScheduleSession;
  action: ClassAction;
  showColor: boolean;
}) {
  const { t, fmt, Link } = useBooking();
  const started = hasStarted(session);
  const closed = session.closedFor != null;
  const time = fmt.time(session.startTime);
  const day = fmt.dayLong(session.startTime);

  const body = (
    <>
      <span className="flex items-baseline justify-between gap-1 tabular-nums">
        <span className="text-sm font-semibold text-ink">{time}</span>
        <span className="text-[11px] text-ashe">
          {t("schedule.duration", { minutes: durationMinutes(session) })}
        </span>
      </span>
      <span className="flex items-center gap-1.5 text-[13px] font-semibold leading-snug text-ink">
        {showColor && <ClassDot color={session.color} />}
        <span className={`min-w-0 break-words ${closed ? "text-ink-soft line-through" : ""}`}>
          {session.title}
        </span>
      </span>
      {session.teacherName && (
        <span className="truncate text-xs text-ink-soft">
          {t("schedule.withTeacher", { name: session.teacherName })}
        </span>
      )}
      <span className="flex flex-wrap items-center gap-1">
        {closed ? (
          <span className="text-xs font-medium text-warning-600">
            {session.closedFor
              ? t("schedule.closed", { name: session.closedFor })
              : t("schedule.closedNoName")}
          </span>
        ) : started ? (
          <span className="text-xs font-medium text-ashe">{t("schedule.ended")}</span>
        ) : action.kind === "booked" ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-success-700">
            <Check size={12} aria-hidden="true" />
            {t("schedule.booked")}
          </span>
        ) : (
          <TileSpots session={session} />
        )}
        {session.isOnline && (
          <span className="rounded-pill bg-info-soft px-1.5 text-[11px] font-semibold text-info">
            {t("schedule.online")}
          </span>
        )}
      </span>
    </>
  );

  const base =
    "flex w-full flex-col gap-1 rounded-control border border-line bg-surface p-2.5 text-left";

  if (started || closed || action.kind === "none" || action.kind === "booked") {
    return <div className={`${base} ${started ? "opacity-60" : ""}`}>{body}</div>;
  }

  const interactive = `${base} transition-colors duration-150 hover:border-line-strong ${focusRing}`;

  if (action.kind === "book") {
    return (
      <Link
        href={action.href}
        aria-label={t("schedule.bookLabel", { title: session.title, day, time })}
        onClick={
          action.onBook
            ? (event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                event.preventDefault();
                action.onBook!();
              }
            : undefined
        }
        className={interactive}
      >
        {body}
      </Link>
    );
  }

  const label = t("schedule.waitlistLabel", { title: session.title, day, time });
  if (action.href) {
    return (
      <Link href={action.href} aria-label={label} className={interactive}>
        {body}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={action.onJoin}
      disabled={action.pending}
      aria-busy={action.pending || undefined}
      aria-label={label}
      className={`${interactive} disabled:opacity-60`}
    >
      {body}
    </button>
  );
}

function TileSpots({ session }: { session: ScheduleSession }) {
  const { t } = useBooking();
  const state = spotsState(session);

  switch (state.kind) {
    case "open":
      return null;
    case "full":
      return (
        <span className="text-xs font-medium text-danger">
          {state.canJoinWaitlist ? t("schedule.fullCanWaitlist") : t("schedule.full")}
        </span>
      );
    case "few":
      return (
        <span className="text-xs font-medium text-warning-700">
          {t("schedule.spotsShort", { count: state.count })}
        </span>
      );
    default:
      return (
        <span className="text-xs text-ashe">{t("schedule.spotsShort", { count: state.count })}</span>
      );
  }
}
